/* eslint-disable react/prop-types */
import { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, Box, CircularProgress } from '@mui/material';
import LandingPage from './pages/LandingPage.js';
import SignInPage from './pages/SignInPage.js';
import SignUpPage from './pages/SignUpPage.js';
import ForgotPasswordPage from './pages/ForgotPasswordPage.js';
import ResetPasswordPage from './pages/ResetPasswordPage.js';
import InviteSignUpPage from './pages/InviteSignUpPage.js';
import UserManagementPage from './pages/UserManagementPage.js';
import MonitorDashboardPage from './pages/MonitorDashboardPage.js';
import Dashboard from './pages/Dashboard.js';
import AdminCourseDetailsPage from './pages/AdminCourseDetailsPage.js';
import CursosDisponiveisPage from './pages/CursosDisponiveisPage.js';
import InscricaoCursoPage from './pages/InscricaoCursoPage.js';
import CursoConteudoPage from './pages/CursoConteudoPage.js';
import StaffCourseViewerPage from './pages/StaffCourseViewerPage.js';
import CertificateValidationPage from './pages/CertificateValidationPage.js';
import TermosDeUsoPage from './pages/TermosDeUsoPage.js';
import CentralAjudaPage from './pages/CentralAjudaPage.js';
import PoliticaPrivacidadePage from './pages/PoliticaPrivacidadePage.js';
import TermosConfidencialidadePage from './pages/TermosConfidencialidadePage.js';
import StudentAreaPage from './pages/StudentAreaPage.js';
import MaintenancePage from './pages/MaintenancePage.js';
import NavBar from './components/NavBar.js';
import Footer from './components/Footer.js';
import CookieConsent from './components/CookieConsent.js';
import {
  AuthProvider,
  AuthBootstrapGate,
  useAuth,
} from './context/AuthContext.js';

/** UX-only home by role — authorization is enforced by the API (403). */
const redirectPathForRole = userRole => {
  if (userRole === 'ADM') return '/dashboard';
  if (userRole === 'COR' || userRole === 'PRO') return '/';
  if (userRole === 'MON') return '/monitor-dashboard';
  if (userRole === 'MBR') return '/cursos';
  return '/';
};

const theme = createTheme({
  // ... (rest of theme)
  // ...

  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
  },
});

const ProtectedRoute = ({
  children,
  allowedRoles = [],
  disablePadding = false,
  hideFooter = false,
  fullHeight = false,
}) => {
  const location = useLocation();
  const { isBootstrapping, isAuthenticated, userRole } = useAuth();

  if (isBootstrapping) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  // Role gate is UX-only; APIs enforce real authorization (403).
  if (allowedRoles.length > 0 && userRole && !allowedRoles.includes(userRole)) {
    return <Navigate to={redirectPathForRole(userRole)} replace />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        ...(fullHeight && { height: '100vh', overflow: 'hidden' }),
      }}
    >
      <NavBar />
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          py: disablePadding ? 0 : 4,
          px: disablePadding ? 0 : 0, // Ensure no horizontal padding on Box if disabled
          ...(fullHeight && {
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            pt: { xs: '56px', sm: '64px' },
            pb: 0,
          }),
        }}
      >
        {disablePadding ? (
          children
        ) : (
          <Container maxWidth={false}>{children}</Container>
        )}
      </Box>
      {!hideFooter && <Footer />}
    </Box>
  );
};

const RoleBasedRedirect = () => {
  const { isBootstrapping, isAuthenticated, userRole } = useAuth();

  if (isBootstrapping) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '40vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/' replace />;
  }

  return <Navigate to={redirectPathForRole(userRole)} replace />;
};

const AdminProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { isBootstrapping, isAuthenticated, userRole } = useAuth();

  if (isBootstrapping) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  // UX-only admin check
  if (userRole !== 'ADM') {
    return <Navigate to='/' replace />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <NavBar />
      <Box component='main' sx={{ flexGrow: 1, py: 4 }}>
        <Container maxWidth={false}>{children}</Container>
      </Box>
      <Footer />
    </Box>
  );
};

const PublicLayout = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    <NavBar />
    <Box component='main' sx={{ flexGrow: 1 }}>
      {children}
    </Box>
    <Footer />
  </Box>
);

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AuthBootstrapGate>
            <ScrollToTop />
            <CookieConsent />
            <Routes>
              <Route
                path='/manutencao'
                element={<MaintenancePage />}
              />
              <Route
                path='/'
                element={
                  <PublicLayout>
                    <LandingPage />
                  </PublicLayout>
                }
              />
              <Route
                path='/login'
                element={
                  <PublicLayout>
                    <SignInPage />
                  </PublicLayout>
                }
              />
              <Route
                path='/signup'
                element={
                  <PublicLayout>
                    <SignUpPage />
                  </PublicLayout>
                }
              />
              <Route
                path='/cadastro'
                element={
                  <PublicLayout>
                    <InviteSignUpPage />
                  </PublicLayout>
                }
              />
              <Route
                path='/forgot-password'
                element={
                  <PublicLayout>
                    <ForgotPasswordPage />
                  </PublicLayout>
                }
              />
              <Route
                path='/reset-password'
                element={
                  <PublicLayout>
                    <ResetPasswordPage />
                  </PublicLayout>
                }
              />
              <Route
                path='/termos'
                element={
                  <PublicLayout>
                    <TermosDeUsoPage />
                  </PublicLayout>
                }
              />
              <Route
                path='/central-ajuda'
                element={
                  <PublicLayout>
                    <CentralAjudaPage />
                  </PublicLayout>
                }
              />
              <Route
                path='/privacidade'
                element={
                  <PublicLayout>
                    <PoliticaPrivacidadePage />
                  </PublicLayout>
                }
              />
              <Route
                path='/termos-de-uso'
                element={
                  <PublicLayout>
                    <TermosDeUsoPage />
                  </PublicLayout>
                }
              />
              <Route
                path='/termos-de-confidencialidade'
                element={
                  <PublicLayout>
                    <TermosConfidencialidadePage />
                  </PublicLayout>
                }
              />
              <Route
                path='/certificados/validar/:validationCode'
                element={
                  <PublicLayout>
                    <CertificateValidationPage />
                  </PublicLayout>
                }
              />
              <Route
                path='/confidencialidade'
                element={
                  <PublicLayout>
                    <TermosConfidencialidadePage />
                  </PublicLayout>
                }
              />
              <Route
                path='/users'
                element={
                  <ProtectedRoute allowedRoles={['COR', 'MON']}>
                    <UserManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/dashboard'
                element={
                  <ProtectedRoute allowedRoles={['ADM']}>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/admin/curso/:id'
                element={
                  <ProtectedRoute allowedRoles={['ADM']}>
                    <AdminCourseDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/cursos'
                element={
                  <ProtectedRoute allowedRoles={['MBR']} disablePadding={true}>
                    <CursosDisponiveisPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/curso/:id/inscricao'
                element={
                  <ProtectedRoute allowedRoles={['MBR']}>
                    <InscricaoCursoPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/curso/:id/conteudo'
                element={
                  <ProtectedRoute
                    allowedRoles={['MBR']}
                    disablePadding={true}
                  >
                    <CursoConteudoPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/minha-area'
                element={
                  <ProtectedRoute
                    allowedRoles={['ADM', 'MON', 'MBR']}
                    disablePadding={true}
                  >
                    <StudentAreaPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path='/curso/versao/:cursoVersaoId/visualizar'
                element={
                  <ProtectedRoute
                    allowedRoles={['ADM', 'MON', 'PRO', 'COR']}
                    disablePadding={true}
                  >
                    <StaffCourseViewerPage />
                  </ProtectedRoute>
                }
              />
              <Route path='/home' element={<RoleBasedRedirect />} />
              <Route
                path='/monitor-dashboard'
                element={
                  <ProtectedRoute allowedRoles={['ADM', 'MON']}>
                    <MonitorDashboardPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </AuthBootstrapGate>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
